import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import { toast } from 'sonner';
import api from '../lib/api';
import { normalizeImageUrl } from '../lib/utils';
import DietIndicator, { DIET_TYPES } from '../components/DietIndicator';
import { ShoppingCart, Plus, Minus, Loader2, ChefHat, Sparkles, Receipt, ArrowRight, BellRing, SlidersHorizontal, BookOpen } from 'lucide-react';

const formatCurrency = (value = 0) => `₹${Number(value || 0).toFixed(2)}`;
const DIET_FILTERS = ['veg', 'non_veg', 'egg', 'vegan'];

const orderStatusConfig = {
  pending: { label: 'Pending', className: 'bg-slate-100 text-slate-700' },
  accepted: { label: 'Preparing', className: 'bg-orange-100 text-orange-700' },
  prepared: { label: 'Served', className: 'bg-emerald-100 text-emerald-700' },
  served: { label: 'Served', className: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
};

const CustomerMenu = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [openCategoryIds, setOpenCategoryIds] = useState([]);
  const [orderedItemsOpen, setOrderedItemsOpen] = useState(false);
  const [orderedOrders, setOrderedOrders] = useState([]);
  const [orderedOrdersLoading, setOrderedOrdersLoading] = useState(false);
  const [assistanceRequest, setAssistanceRequest] = useState(null);
  const [requestingAssistance, setRequestingAssistance] = useState(false);
  const [dietFilters, setDietFilters] = useState({ veg: true, non_veg: true, egg: true, vegan: true });
  const [sortOption, setSortOption] = useState('none');
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    const sessionToken = localStorage.getItem('customer_session');
    const storedTableId = localStorage.getItem('customer_table_id');

    if (!sessionToken || storedTableId !== tableId) {
      toast.error('Please scan the QR code again to continue.');
      navigate(`/customer/${tableId}`, { replace: true });
      return;
    }

    fetchMenu();
  }, [navigate, tableId]);

  const fetchMenu = async () => {
    try {
      const sessionToken = localStorage.getItem('customer_session') || '';
      const [catRes, itemsRes, ordersRes] = await Promise.all([
        api.get('/api/menu/categories', {
          params: {
            customer_session_token: sessionToken,
            table_id: tableId,
          },
        }),
        api.get('/api/menu/items', {
          params: {
            customer_session_token: sessionToken,
            table_id: tableId,
          },
        }),
        api.get('/api/customer/orders', {
          params: {
            customer_session_token: sessionToken,
          },
        }),
      ]);
      
      setCategories(catRes.data);
      setMenuItems(itemsRes.data.filter(item => item.available));
      setOrderedOrders(ordersRes.data.orders || []);
      setAssistanceRequest(ordersRes.data.assistance_request || null);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load menu');
      setLoading(false);
    }
  };

  const fetchOrderedOrders = async ({ showLoader = false } = {}) => {
    const sessionToken = localStorage.getItem('customer_session') || '';
    if (!sessionToken) return;

    if (showLoader) setOrderedOrdersLoading(true);
    try {
      const response = await api.get('/api/customer/orders', {
        params: {
          customer_session_token: sessionToken,
        },
      });
      setOrderedOrders(response.data.orders || []);
      setAssistanceRequest(response.data.assistance_request || null);
    } catch (error) {
      toast.error('Failed to load ordered items');
    } finally {
      if (showLoader) setOrderedOrdersLoading(false);
    }
  };

  const openOrderedItems = () => {
    setOrderedItemsOpen(true);
    fetchOrderedOrders({ showLoader: true });
  };

  const requestAssistance = async () => {
    if (requestingAssistance || assistanceRequest) return;

    const sessionToken = localStorage.getItem('customer_session');
    if (!sessionToken) {
      toast.error('Session expired. Please scan the QR code again.');
      navigate(`/customer/${tableId}`);
      return;
    }

    setRequestingAssistance(true);
    try {
      const response = await api.post(
        '/api/customer/assistance',
        { customer_session_token: sessionToken },
        { params: { customer_session_token: sessionToken } }
      );
      setAssistanceRequest(response.data);
      toast.success('Staff assistance requested.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request assistance');
    } finally {
      setRequestingAssistance(false);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.item_id === item.item_id);
    if (existing) {
      setCart(cart.map((c) => c.item_id === item.item_id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { ...item, quantity: 1, instructions: '' }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const updateQuantity = (item_id, delta) => {
    setCart(cart.map((c) => {
      if (c.item_id === item_id) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const placeOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const sessionToken = localStorage.getItem('customer_session');
    if (!sessionToken) {
      toast.error('Session expired');
      navigate(`/customer/${tableId}`);
      return;
    }

    setSubmitting(true);
    try {
      const orderItems = cart.map((c) => ({
        item_id: c.item_id,
        quantity: c.quantity,
        instructions: c.instructions || '',
      }));

      const response = await api.post(`/api/orders`, {
        customer_session_token: sessionToken,
        items: orderItems,
      });

      setCartOpen(false);
      toast.success('Order placed successfully!');
      navigate(`/customer/order/${response.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const getItemDietType = (item) => (DIET_TYPES[item?.diet_type] ? item.diet_type : 'veg');
  const availableDietFilterKeys = DIET_FILTERS.filter((filterKey) => (
    menuItems.some((item) => getItemDietType(item) === filterKey)
  ));
  const visibleDietFilterKeys = availableDietFilterKeys.length > 0 ? availableDietFilterKeys : ['veg'];

  const filteredMenuItems = menuItems.filter((item) => {
    const itemDietType = getItemDietType(item);
    return Boolean(dietFilters[itemDietType]);
  }).sort((left, right) => {
    if (sortOption === 'price_low_high') {
      return Number(left.price || 0) - Number(right.price || 0);
    }
    if (sortOption === 'price_high_low') {
      return Number(right.price || 0) - Number(left.price || 0);
    }
    return 0;
  });

  const accordionCategories = categories.map((category) => ({
    ...category,
    items: filteredMenuItems.filter((item) => item.category_id === category.category_id),
  })).filter((category) => category.items.length > 0);

  useEffect(() => {
    if (accordionCategories.length === 0) {
      setOpenCategoryIds([]);
      return;
    }

    setOpenCategoryIds((current) => {
      const visibleIds = accordionCategories.map((category) => category.category_id);
      const stillVisible = current.filter((categoryId) => visibleIds.includes(categoryId));
      return stillVisible.length > 0 ? stillVisible : visibleIds.slice(0, 2);
    });
  }, [accordionCategories.length, categories.length, menuItems.length, dietFilters.veg, dietFilters.non_veg, dietFilters.egg, dietFilters.vegan, sortOption]);

  const toggleDietFilter = (filterKey) => {
    setDietFilters((current) => {
      const visibleKeys = visibleDietFilterKeys.length > 0 ? visibleDietFilterKeys : DIET_FILTERS;
      const allVisibleActive = visibleKeys.every((key) => current[key]);
      const onlyThisVisibleActive = visibleKeys.every((key) => (
        key === filterKey ? current[key] : !current[key]
      ));

      if (allVisibleActive) {
        return DIET_FILTERS.reduce((next, key) => ({
          ...next,
          [key]: key === filterKey,
        }), {});
      }

      if (onlyThisVisibleActive) {
        return DIET_FILTERS.reduce((next, key) => ({
          ...next,
          [key]: visibleKeys.includes(key),
        }), {});
      }

      return {
        ...current,
        [filterKey]: !current[filterKey],
      };
    });
  };

  const showAllDietFilters = () => {
    const visibleKeys = visibleDietFilterKeys.length > 0 ? visibleDietFilterKeys : DIET_FILTERS;
    setDietFilters(DIET_FILTERS.reduce((next, key) => ({
      ...next,
      [key]: visibleKeys.includes(key),
    }), {}));
  };

  const clearFiltersAndSort = () => {
    showAllDietFilters();
    setSortOption('none');
  };

  const jumpToCategory = (categoryId) => {
    setOpenCategoryIds((current) => (
      current.includes(categoryId) ? current : [...current, categoryId]
    ));
    setCategoryOpen(false);
    window.setTimeout(() => {
      const titleElement = document.getElementById(`menu-category-title-${categoryId}`);
      if (!titleElement) return;
      const stickyHeaderHeight = document.querySelector('[data-customer-menu-header]')?.offsetHeight || 0;
      const top = titleElement.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight - 16;
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: 'smooth',
      });
    }, 120);
  };

  const markImageBroken = (itemId) => {
    setBrokenImages((prev) => ({ ...prev, [itemId]: true }));
  };

  const toggleDescription = (itemId) => {
    setExpandedDescriptions((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const orderedItemCount = orderedOrders.reduce(
    (sum, order) => sum + (order.items || []).reduce((itemSum, item) => itemSum + Number(item.quantity || 0), 0),
    0
  );
  const orderedTotal = orderedOrders.reduce((sum, order) => sum + Number(order.total || 0), 0);
  const getCartItem = (itemId) => cart.find((item) => item.item_id === itemId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9F8F6' }}>
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28 sm:pb-32" style={{ background: '#F9F8F6' }}>
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10" data-customer-menu-header>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <ChefHat className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Our Menu</h1>
              <p className="text-sm text-muted-foreground">Table {tableId}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 rounded-[28px] border border-border bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.05)]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground">Food Ordering</p>
              <h2 className="text-2xl font-semibold tracking-tight">Browse by category</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={requestAssistance}
                disabled={requestingAssistance || Boolean(assistanceRequest)}
                className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-80"
              >
                {requestingAssistance ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <BellRing className="h-4 w-4" />
                )}
                {assistanceRequest ? 'Assistance Requested' : 'Request Assistance'}
              </button>
              {orderedOrders.length > 0 && (
                <button
                  type="button"
                  onClick={openOrderedItems}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm transition hover:bg-primary/5"
                >
                  <Receipt className="h-4 w-4" />
                  Ordered Items
                  <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-white">{orderedItemCount}</span>
                </button>
              )}
              <div className="flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" />
                Freshly prepared for your table
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5 flex items-center gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-white px-3 py-2 text-sm font-semibold text-foreground shadow-sm transition hover:bg-accent"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
          </button>
          {visibleDietFilterKeys.map((filterKey) => {
            const filterConfig = DIET_TYPES[filterKey];
            const active = dietFilters[filterKey];

            return (
              <button
                key={filterKey}
                type="button"
                aria-pressed={active}
                onClick={() => toggleDietFilter(filterKey)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition ${
                  active
                    ? filterConfig.activeClass
                    : 'border-border bg-white text-muted-foreground'
                }`}
              >
                <DietIndicator item={{ diet_type: filterKey }} />
                {filterConfig.label}
              </button>
            );
          })}
        </div>

        {accordionCategories.length > 0 ? (
          <Accordion
            type="multiple"
            value={openCategoryIds}
            onValueChange={setOpenCategoryIds}
            className="space-y-4"
          >
            {accordionCategories.map((category) => (
            <AccordionItem
              key={category.category_id}
              value={category.category_id}
              id={`menu-category-${category.category_id}`}
              className="rounded-[28px] border border-border bg-white px-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
            >
              <AccordionTrigger className="py-5 no-underline hover:no-underline">
                <div className="flex min-w-0 flex-1 items-center justify-between gap-4 pr-4">
                  <div id={`menu-category-title-${category.category_id}`} className="min-w-0 scroll-mt-24">
                    <h3 className="text-left text-xl font-semibold tracking-tight">{category.name}</h3>
                    <p className="text-left text-sm text-muted-foreground">{category.items.length} items</p>
                  </div>
                  <Badge className="rounded-full bg-accent text-foreground">{category.items.length}</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                 <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
                  {category.items.map((item) => (
                    (() => {
                      const showImage = item.image && !brokenImages[item.item_id];
                      const hasLongDescription = item.description && item.description.trim().length > 72;
                      const isDescriptionExpanded = !!expandedDescriptions[item.item_id];
                      return (
                        <Card
                          key={item.item_id}
                          className="overflow-hidden rounded-[22px] border border-border bg-[#FCFBF8] shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
                          data-testid={`menu-item-${item.item_id}`}
                        >
                          {showImage && (
                            <div className="relative h-36 overflow-hidden bg-accent/20 sm:h-40">
                              <img
                                src={normalizeImageUrl(item.image)}
                                alt={item.name}
                                className="h-full w-full object-cover"
                                onError={() => markImageBroken(item.item_id)}
                              />
                              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/20 to-transparent" />
                              <div className="absolute bottom-3 right-3">
                                {getCartItem(item.item_id) ? (
                                  <div className="inline-flex items-center gap-1 rounded-full bg-white/95 p-1 shadow-lg">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                                      onClick={() => updateQuantity(item.item_id, -1)}
                                      data-testid={`decrease-qty-${item.item_id}`}
                                    >
                                      <Minus className="h-3.5 w-3.5" />
                                    </Button>
                                    <span className="min-w-[1.25rem] text-center text-xs font-semibold text-primary">
                                      {getCartItem(item.item_id)?.quantity || 0}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                                      onClick={() => addToCart(item)}
                                      data-testid={`increase-qty-${item.item_id}`}
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={() => addToCart(item)}
                                    className="h-9 rounded-full bg-primary px-4 text-sm text-white shadow-lg hover:bg-[#C54E2C]"
                                    data-testid={`add-to-cart-${item.item_id}`}
                                  >
                                    Add
                                  </Button>
                                )}
                              </div>    
                            </div>
                          )}
                             {!showImage && (
                            <div className="flex justify-end px-3.5 pt-3.5">
                              {getCartItem(item.item_id) ? (
                                <div className="inline-flex items-center gap-1 rounded-full bg-white p-1 shadow-md">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                                    onClick={() => updateQuantity(item.item_id, -1)}
                                    data-testid={`decrease-qty-${item.item_id}`}
                                  >
                                    <Minus className="h-3.5 w-3.5" />
                                  </Button>
                                  <span className="min-w-[1.25rem] text-center text-xs font-semibold text-primary">
                                    {getCartItem(item.item_id)?.quantity || 0}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                                    onClick={() => addToCart(item)}
                                    data-testid={`increase-qty-${item.item_id}`}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => addToCart(item)}
                                  className="h-9 rounded-full bg-primary px-4 text-sm text-white shadow-md hover:bg-[#C54E2C]"
                                  data-testid={`add-to-cart-${item.item_id}`}
                                >
                                  Add
                                </Button>
                              )}
                            </div>
                          )}
                           <div className="flex flex-col gap-2 p-3.5">
                            <div className="min-w-0">
	                              <div className="flex items-start gap-2">
	                                <DietIndicator item={item} className="mt-1" />
	                                <h4 className="line-clamp-2 break-words text-base font-semibold leading-tight">{item.name}</h4>
	                              </div>
                              {item.description && (
                                 <div className="mt-1 space-y-1.5">
                                  <p
                                    className={`break-words text-xs leading-5 text-muted-foreground sm:text-sm ${
                                      isDescriptionExpanded ? '' : 'line-clamp-2'
                                    }`}
                                  >
                                    {item.description}
                                  </p>
                                  {hasLongDescription && (
                                    <button
                                      type="button"
                                      onClick={() => toggleDescription(item.item_id)}
                                      className="text-xs font-semibold text-primary transition-colors hover:text-[#C54E2C]"
                                      data-testid={`toggle-description-${item.item_id}`}
                                    >
                                      {isDescriptionExpanded ? 'Less' : 'More'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                        <p className="text-lg font-bold text-primary sm:text-xl">₹{item.price}</p>
                          </div>
                        </Card>
                      );
                    })()
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="rounded-[24px] border border-border bg-white p-6 text-center text-sm font-medium text-muted-foreground shadow-sm">
            {menuItems.length > 0 ? 'No items match the selected filters.' : 'No available menu items were found for this restaurant yet.'}
          </div>
        )}

      </div>

      <Drawer open={filterOpen} onOpenChange={setFilterOpen}>
        <DrawerContent className="mx-auto max-h-[88dvh] max-w-3xl rounded-t-[28px] border-border bg-[#F6F7FA]">
          <DrawerHeader className="border-b border-border px-5 pb-4 pt-2 text-left">
            <DrawerTitle className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Filters and Sorting
            </DrawerTitle>
            <DrawerDescription>
              Refine the menu items shown for this table.
            </DrawerDescription>
          </DrawerHeader>

          <div className="space-y-5 overflow-y-auto px-5 py-5">
            <section className="rounded-[26px] bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <h3 className="mb-4 text-xl font-semibold tracking-tight text-slate-900">Sort by</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { value: 'price_low_high', label: 'Price - low to high' },
                  { value: 'price_high_low', label: 'Price - high to low' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={sortOption === option.value}
                    onClick={() => setSortOption((current) => (current === option.value ? 'none' : option.value))}
                    className={`rounded-2xl border px-4 py-3 text-left text-base font-semibold shadow-sm transition sm:text-lg ${
                      sortOption === option.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-white text-slate-800 hover:bg-accent'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-[26px] bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
              <h3 className="mb-4 text-xl font-semibold tracking-tight text-slate-900">Food preference</h3>
              <div className="flex flex-wrap gap-3">
                {visibleDietFilterKeys.map((filterKey) => {
                  const filterConfig = DIET_TYPES[filterKey];
                  const active = dietFilters[filterKey];

                  return (
                    <button
                      key={filterKey}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleDietFilter(filterKey)}
                      className={`inline-flex min-h-12 items-center gap-2 rounded-2xl border px-4 text-base font-semibold shadow-sm transition ${
                        active
                          ? filterConfig.activeClass
                          : 'border-border bg-white text-slate-500 hover:bg-accent'
                      }`}
                    >
                      <DietIndicator item={{ diet_type: filterKey }} />
                      {filterConfig.label}
                    </button>
                  );
                })}
              </div>
            </section>
          </div>

          <DrawerFooter className="grid grid-cols-2 gap-3 border-t border-border bg-white px-5 pb-5 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={clearFiltersAndSort}
              className="h-12 rounded-2xl text-base font-semibold"
            >
              Clear
            </Button>
            <Button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="h-12 rounded-2xl bg-primary text-base font-semibold text-white hover:bg-[#C54E2C]"
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
        <DialogContent className="max-h-[78dvh] max-w-[min(88vw,430px)] overflow-hidden rounded-[30px] border-0 bg-[#02070C] p-0 text-white shadow-[0_24px_70px_rgba(0,0,0,0.45)]">
          <DialogHeader className="sr-only">
            <DialogTitle>Menu categories</DialogTitle>
            <DialogDescription>Select a category to jump to that item section.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[78dvh] overflow-y-auto px-7 py-7">
            <div className="space-y-1">
              {accordionCategories.map((category) => {
                const active = openCategoryIds.includes(category.category_id);

                return (
                  <button
                    key={category.category_id}
                    type="button"
                    onClick={() => jumpToCategory(category.category_id)}
                    className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-2xl px-2 py-3 text-left transition hover:bg-white/10 ${
                      active ? 'text-white' : 'text-white/72'
                    }`}
                  >
                    <span className={`truncate text-xl sm:text-2xl ${active ? 'font-bold' : 'font-medium'}`}>
                      {category.name}
                    </span>
                    <span className={`text-xl sm:text-2xl ${active ? 'font-bold' : 'font-medium'}`}>
                      {category.items.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Drawer open={orderedItemsOpen} onOpenChange={setOrderedItemsOpen}>
        <DrawerContent className="mx-auto max-h-[88dvh] max-w-3xl rounded-t-[28px] border-border bg-[#FCFBF8]">
          <DrawerHeader className="border-b border-border px-5 pb-4 pt-2 text-left">
            <DrawerTitle className="text-2xl font-bold tracking-tight">Ordered Items</DrawerTitle>
            <DrawerDescription>
              Track the items already placed for this table.
            </DrawerDescription>
          </DrawerHeader>

          <div className="max-h-[58dvh] space-y-4 overflow-y-auto px-5 py-4">
            {orderedOrdersLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : orderedOrders.length > 0 ? (
              orderedOrders.map((order) => {
                const status = orderStatusConfig[order.status] || orderStatusConfig.pending;
                return (
                  <div key={order.order_id} className="rounded-[22px] border border-border bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="break-words text-sm font-normal text-muted-foreground">Order #{order.order_id}</h3>
                          <Badge className={`rounded-full ${status.className}`}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {order.table_label || `Table ${tableId}`} • {new Date(order.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 shrink-0 rounded-full px-4"
                        onClick={() => navigate(`/customer/order/${order.order_id}`)}
                      >
                        Track
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mt-4 space-y-2">
                      {(order.items || []).map((item, index) => (
                        <div
                          key={`${order.order_id}-${item.item_id || item.name}-${index}`}
                          className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-2xl bg-accent px-3 py-3"
                        >
                          <div className="min-w-0">
	                            <div className="flex items-start gap-2">
	                              <DietIndicator item={item} className="mt-0.5" />
	                              <p className="break-words text-sm font-semibold">{item.name}</p>
	                            </div>
                            <p className="text-xs text-muted-foreground">Quantity: {item.quantity}</p>
                            {item.instructions && (
                              <p className="mt-1 break-words text-xs italic text-muted-foreground">
                                Note: {item.instructions}
                              </p>
                            )}
                          </div>
                          <p className="shrink-0 text-sm font-bold text-primary">
                            {formatCurrency(Number(item.price || 0) * Number(item.quantity || 0))}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                      <span className="text-sm font-medium text-muted-foreground">
                        {(order.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0)} item(s)
                      </span>
                      <span className="text-base font-bold text-primary">{formatCurrency(order.total)}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-[22px] border border-dashed border-border bg-white p-8 text-center">
                <Receipt className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 font-semibold">No ordered items yet</p>
                <p className="mt-1 text-sm text-muted-foreground">Items you place will appear here.</p>
              </div>
            )}
          </div>

          {orderedOrders.length > 0 && (
            <DrawerFooter className="border-t border-border bg-white px-5 pb-5 pt-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{orderedItemCount} item{orderedItemCount === 1 ? '' : 's'} ordered</span>
                <span>Total</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold tracking-tight">Current order total</span>
                <span className="text-xl font-bold text-primary">{formatCurrency(orderedTotal)}</span>
              </div>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {/* Sticky Cart */}
      {cart.length > 0 && (
        <>
          <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-border bg-white/75 p-4 backdrop-blur-xl">
            <div className="mx-auto max-w-6xl">
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                className="flex w-full items-center justify-between rounded-[22px] bg-primary px-5 py-4 text-left text-white shadow-[0_16px_40px_rgba(214,103,62,0.28)] transition-transform hover:translate-y-[-1px]"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-semibold">{cartItemCount} item{cartItemCount === 1 ? '' : 's'} added</p>
                    <p className="text-sm text-white/80">Tap to review your order</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">₹{cartTotal.toFixed(2)}</p>
                  <p className="text-sm font-medium text-white/90">View cart</p>
                </div>
              </button>
            </div>
          </div>

          <Drawer open={cartOpen} onOpenChange={setCartOpen}>
            <DrawerContent className="mx-auto max-h-[88dvh] max-w-3xl rounded-t-[28px] border-border bg-[#FCFBF8]">
              <DrawerHeader className="border-b border-border px-5 pb-4 pt-2 text-left">
                <DrawerTitle className="text-2xl font-bold tracking-tight">Your order</DrawerTitle>
                <DrawerDescription>
                  Review items, adjust quantity, and place your order.
                </DrawerDescription>
              </DrawerHeader>

              <div className="max-h-[52dvh] space-y-3 overflow-y-auto px-5 py-4">
                {cart.map((item) => (
                  <div key={item.item_id} className="rounded-[22px] border border-border bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
	                        <div className="flex items-start gap-2">
	                          <DietIndicator item={item} className="mt-1" />
	                          <h4 className="break-words text-base font-semibold">{item.name}</h4>
	                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">₹{item.price} each</p>
                      </div>
                      <p className="shrink-0 text-lg font-bold text-primary">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 p-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                          onClick={() => updateQuantity(item.item_id, -1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="min-w-[1.75rem] text-center text-sm font-semibold text-primary">{item.quantity}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 rounded-full p-0 text-primary hover:bg-primary/10 hover:text-primary"
                          onClick={() => updateQuantity(item.item_id, 1)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <DrawerFooter className="border-t border-border bg-white px-5 pb-5 pt-4">
                <div className="mb-1 flex items-center justify-between text-sm text-muted-foreground">
                  <span>{cartItemCount} item{cartItemCount === 1 ? '' : 's'}</span>
                  <span>Total</span>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-2xl font-bold tracking-tight">Order total</span>
                  <span className="text-2xl font-bold text-primary">₹{cartTotal.toFixed(2)}</span>
                </div>
                <Button
                  onClick={placeOrder}
                  disabled={submitting}
                  className="h-12 w-full rounded-full bg-primary text-base text-white hover:bg-[#C54E2C]"
                  data-testid="place-order-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Placing Order...
                    </>
                  ) : (
                    'Place Order'
                  )}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </>
      )}

      {accordionCategories.length > 0 && (
        <button
          type="button"
          onClick={() => setCategoryOpen(true)}
          className={`fixed right-4 z-30 flex h-20 w-20 flex-col items-center justify-center rounded-full bg-[#02070C] text-white shadow-[0_18px_44px_rgba(0,0,0,0.35)] transition hover:scale-[1.02] active:scale-95 sm:right-6 ${
            cartItemCount > 0 ? 'bottom-32 sm:bottom-36' : 'bottom-6'
          }`}
          aria-label="Show menu categories"
        >
          <BookOpen className="h-6 w-6" />
          <span className="mt-1 text-xs font-black tracking-wide">MENU</span>
        </button>
      )}
    </div>
  );
};

export default CustomerMenu;
